// src/pages/lms/LMSReports.jsx — COMPLETE ANALYTICS
// Real-time data from every LMS feature
// Charts: attendance, fees, library, assignments, degrees, years
// No external chart libs needed — pure CSS/SVG bars
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import {
  Users, BookOpen, GraduationCap, CreditCard, Library,
  BarChart3, TrendingUp, RefreshCw, Calendar, Bell,
  CheckCircle, AlertCircle, Clock, Award, FileText,
} from 'lucide-react';

// ── Mini bar chart ─────────────────────────────────────
function BarChart({ data, color = '#4F8EF7', height = 120, showLabels = true }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:6, height, paddingTop:8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4, height:'100%', justifyContent:'flex-end' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#374151' }}>{d.value || ''}</div>
          <div style={{
            width:'100%', borderRadius:'4px 4px 0 0',
            background: typeof color === 'function' ? color(d, i) : color,
            height: `${Math.max((d.value / max) * (height - 24), d.value > 0 ? 6 : 0)}px`,
            minHeight: d.value > 0 ? 6 : 0,
            transition: 'height 0.5s ease',
          }}/>
          {showLabels && <div style={{ fontSize:9, color:'#9CA3AF', textAlign:'center', lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{d.label}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Donut chart ────────────────────────────────────────
function DonutChart({ segments, size = 100, thickness = 18 }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  if (total === 0) return <div style={{ width:size, height:size, borderRadius:'50%', background:'#F1F5F9', border:'2px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ fontSize:11, color:'#9CA3AF' }}>No data</span></div>;
  let offset = 0;
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={thickness}/>
      {segments.map((s, i) => {
        const pct  = s.value / total;
        const dash = pct * circ;
        const gap  = circ - dash;
        const el   = (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={s.color} strokeWidth={thickness}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
            style={{ transform:`rotate(-90deg)`, transformOrigin:'50% 50%', transition:'stroke-dasharray 0.5s' }}/>
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

// ── Stat card ──────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color, bg, trend }) {
  return (
    <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E5E7EB', padding:'18px 16px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
        <div style={{ width:42, height:42, borderRadius:12, background:bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon size={20} color={color}/>
        </div>
        {trend !== undefined && (
          <div style={{ fontSize:11, fontWeight:700, color: trend >= 0 ? '#10B981' : '#EF4444' }}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div style={{ fontSize:28, fontWeight:900, color:'#111827', lineHeight:1 }}>{value ?? '—'}</div>
      <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginTop:4 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

// ── Progress row ───────────────────────────────────────
function ProgressRow({ label, value, max, color, right }) {
  const pct = max > 0 ? Math.round(value / max * 100) : 0;
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
        <span style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{label}</span>
        <span style={{ fontSize:13, fontWeight:700, color }}>{right ?? value}</span>
      </div>
      <div style={{ background:'#F1F5F9', borderRadius:6, height:8, overflow:'hidden' }}>
        <div style={{ background:color, borderRadius:6, height:'100%', width:`${pct}%`, transition:'width 0.6s ease' }}/>
      </div>
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────
function Section({ title, icon: Icon, color = '#4F8EF7', children, action }) {
  return (
    <div style={{ background:'#fff', borderRadius:18, border:'1px solid #E5E7EB', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ padding:'16px 20px', borderBottom:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:10, background:`${color}15`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Icon size={17} color={color}/>
          </div>
          <span style={{ fontSize:15, fontWeight:800, color:'#111827' }}>{title}</span>
        </div>
        {action}
      </div>
      <div style={{ padding:20 }}>{children}</div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────
export default function LMSReports() {
  const { institution } = useLMSAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    if (!institution?.id) return;
    setLoading(true);
    const id = institution.id;
    try {
      const [
        studentsRes, teachersRes, classesRes,
        assignmentsRes, submissionsRes,
        attSessionsRes, attRecordsRes,
        feesRes, feeVouchersRes,
        booksRes, borrowsRes,
        eventsRes, announcementsRes, noticesRes,
        feedRes, timetableRes,
        teacherAttRes,
      ] = await Promise.all([
        // Students
        supabase.from('profiles').select('id,degree,year_of_study,gender,created_at')
          .eq('institution_id', id).eq('role', 'student'),
        // Teachers
        supabase.from('lms_teachers').select('id,full_name,department,status,created_at')
          .eq('institution_id', id),
        // Classes
        supabase.from('lms_classes').select('id,name,total_students,is_active')
          .eq('institution_id', id),
        // Assignments
        supabase.from('lms_assignments').select('id,is_published,submissions_count,created_at')
          .eq('institution_id', id),
        // Submissions
        supabase.from('lms_assignment_submissions').select('id,is_graded,is_late')
          .eq('institution_id', id),
        // Attendance sessions
        supabase.from('lms_attendance_sessions').select('id,class_id,session_date,present_count,absent_count,total_students')
          .eq('institution_id', id),
        // Attendance records for class breakdown
        supabase.from('lms_attendance_records').select('class_id,status,session_date')
          .eq('institution_id', id).limit(5000),
        // Fee structures
        supabase.from('lms_fee_structures').select('id,is_active').eq('institution_id', id),
        // Fee vouchers
        supabase.from('lms_fee_vouchers').select('id,status,final_amount,total_amount,submission_status,created_at')
          .eq('institution_id', id),
        // Books
        supabase.from('lms_books').select('id,total_copies,available_copies,category')
          .eq('institution_id', id).eq('is_active', true),
        // Borrows
        supabase.from('lms_borrow_records').select('id,status,fine_amount,fine_paid,student_id,created_at')
          .eq('institution_id', id),
        // Events
        supabase.from('lms_events').select('id,is_published,is_cancelled,created_at').eq('institution_id', id),
        // Announcements
        supabase.from('lms_announcements').select('id,priority,is_published,created_at').eq('institution_id', id),
        // Notices
        supabase.from('lms_notices').select('id,is_published,created_at').eq('institution_id', id),
        // Feed posts
        supabase.from('lms_feed').select('id,created_at').eq('institution_id', id).limit(1),
        // Timetable slots
        supabase.from('lms_timetable').select('id,day_of_week,teacher_id,class_id').eq('institution_id', id).eq('is_active', true),
        // Teacher attendance
        supabase.from('lms_teacher_attendance').select('id,teacher_id,teacher_name,status,scheduled_date').eq('institution_id', id),
      ]);

      // ── Process students ──────────────────────────
      const students = studentsRes.data || [];
      const degreeMap = {}, yearMap = {}, genderMap = {};
      const monthlyStudents = {};
      for (const s of students) {
        if (s.degree) degreeMap[s.degree] = (degreeMap[s.degree]||0)+1;
        if (s.year_of_study) yearMap[s.year_of_study] = (yearMap[s.year_of_study]||0)+1;
        if (s.gender) genderMap[s.gender] = (genderMap[s.gender]||0)+1;
        if (s.created_at) {
          const m = s.created_at.slice(0,7);
          monthlyStudents[m] = (monthlyStudents[m]||0)+1;
        }
      }

      // ── Process teachers ──────────────────────────
      const teachers   = teachersRes.data || [];
      const activeTeachers = teachers.filter(t=>t.status==='active').length;
      const deptMap = {};
      for (const t of teachers) {
        if (t.department) deptMap[t.department] = (deptMap[t.department]||0)+1;
      }

      // ── Process classes ───────────────────────────
      const classes = classesRes.data || [];
      const activeClasses = classes.filter(c=>c.is_active!==false);

      // ── Process attendance ────────────────────────
      const sessions = attSessionsRes.data || [];
      const records  = attRecordsRes.data  || [];
      let totalPresent=0, totalAbsent=0, totalSessions=sessions.length;
      for(const s of sessions){ totalPresent+=s.present_count||0; totalAbsent+=s.absent_count||0; }
      const overallAtt = totalPresent+totalAbsent>0 ? Math.round(totalPresent/(totalPresent+totalAbsent)*100) : 0;

      // Per-class attendance
      const classAttMap = {};
      for(const r of records){
        if(!classAttMap[r.class_id]) classAttMap[r.class_id]={present:0,total:0};
        if(r.status!=='holiday') classAttMap[r.class_id].total++;
        if(r.status==='present'||r.status==='late') classAttMap[r.class_id].present++;
      }
      const classAtt = activeClasses.map(c=>({
        name: c.name,
        pct:  classAttMap[c.id]?.total>0 ? Math.round(classAttMap[c.id].present/classAttMap[c.id].total*100) : 0,
        total: classAttMap[c.id]?.total||0,
      })).filter(c=>c.total>0).sort((a,b)=>b.pct-a.pct);

      // Monthly attendance (last 6 months)
      const monthlyAtt = {};
      for(const s of sessions){
        const m = s.session_date?.slice(0,7);
        if(!m) continue;
        if(!monthlyAtt[m]) monthlyAtt[m]={present:0,total:0};
        monthlyAtt[m].present += s.present_count||0;
        monthlyAtt[m].total   += s.total_students||0;
      }

      // ── Process fees ──────────────────────────────
      const vouchers = feeVouchersRes.data || [];
      const totalCollected = vouchers.filter(v=>v.status==='paid')
        .reduce((a,v)=>a+(Number(v.final_amount||v.total_amount)||0),0);
      const totalPending = vouchers.filter(v=>v.status==='pending'||v.status==='overdue')
        .reduce((a,v)=>a+(Number(v.final_amount||v.total_amount)||0),0);
      const feePaid    = vouchers.filter(v=>v.status==='paid').length;
      const feePending = vouchers.filter(v=>v.status==='pending').length;
      const feeOverdue = vouchers.filter(v=>v.status==='overdue').length;
      const feeSubmitted = vouchers.filter(v=>v.submission_status==='submitted').length;

      // ── Process library ───────────────────────────
      const books   = booksRes.data   || [];
      const borrows = borrowsRes.data || [];
      const totalBooks     = books.reduce((a,b)=>a+b.total_copies,0);
      const availableBooks = books.reduce((a,b)=>a+b.available_copies,0);
      const activeBorrows  = borrows.filter(b=>b.status==='borrowed'||b.status==='overdue').length;
      const overdueBorrows = borrows.filter(b=>b.status==='overdue').length;
      const totalFines     = borrows.reduce((a,b)=>a+(Number(b.fine_amount)||0),0);
      const unpaidFines    = borrows.filter(b=>!b.fine_paid).reduce((a,b)=>a+(Number(b.fine_amount)||0),0);

      // Category breakdown
      const catMap = {};
      for(const b of books) if(b.category) catMap[b.category]=(catMap[b.category]||0)+b.total_copies;

      // ── Process assignments ───────────────────────
      const assignments  = assignmentsRes.data  || [];
      const submissions  = submissionsRes.data  || [];
      const published    = assignments.filter(a=>a.is_published).length;
      const totalSubm    = submissions.length;
      const graded       = submissions.filter(s=>s.is_graded).length;
      const lateSubm     = submissions.filter(s=>s.is_late).length;

      // ── Process teacher attendance ────────────────
      const teacherAtt = teacherAttRes.data || [];
      const teacherAttMap = {};
      for(const a of teacherAtt){
        if(!teacherAttMap[a.teacher_id]) teacherAttMap[a.teacher_id]={name:a.teacher_name,present:0,total:0};
        teacherAttMap[a.teacher_id].total++;
        if(a.status==='present') teacherAttMap[a.teacher_id].present++;
      }
      // Add weekly slots per teacher
      const timetableSlots = timetableRes.data || [];
      const teacherSlots = {};
      for(const t of timetableSlots) if(t.teacher_id) teacherSlots[t.teacher_id]=(teacherSlots[t.teacher_id]||0)+1;

      // ── Process events/comms ──────────────────────
      const events        = eventsRes.data        || [];
      const announcements = announcementsRes.data || [];
      const notices       = noticesRes.data       || [];

      // ── Last 6 months labels ──────────────────────
      const last6 = [];
      for(let i=5;i>=0;i--){
        const d=new Date(); d.setMonth(d.getMonth()-i);
        last6.push(d.toISOString().slice(0,7));
      }

      setData({
        students: students.length, activeTeachers, teachers: teachers.length,
        activeClasses: activeClasses.length,
        degreeMap, yearMap, genderMap, deptMap, monthlyStudents,
        overallAtt, totalSessions, classAtt, monthlyAtt, last6,
        totalCollected, totalPending, feePaid, feePending, feeOverdue, feeSubmitted,
        vouchers: vouchers.length,
        totalBooks, availableBooks, activeBorrows, overdueBorrows, totalFines, unpaidFines, catMap,
        booksCount: books.length,
        published, totalSubm, graded, lateSubm, assignments: assignments.length,
        teacherAttMap, teacherSlots,
        events: events.filter(e=>e.is_published).length,
        announcements: announcements.filter(a=>a.is_published).length,
        notices: notices.filter(n=>n.is_published).length,
      });
    } catch(e) { console.error('Reports load error:', e); }
    finally { setLoading(false); }
  }, [institution?.id, refresh]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return (
    <LMSLayout>
      <LMSPageGuard pageKey="reports">
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:16 }}>
          <div style={{ width:48, height:48, border:'4px solid #E5E7EB', borderTopColor:'#4F8EF7', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
          <div style={{ color:'#9CA3AF', fontSize:14 }}>Loading reports...</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </LMSPageGuard>
    </LMSLayout>
  );

  const {
    students, activeTeachers, teachers, activeClasses,
    degreeMap, yearMap, genderMap, deptMap, monthlyStudents,
    overallAtt, totalSessions, classAtt, monthlyAtt, last6,
    totalCollected, totalPending, feePaid, feePending, feeOverdue, feeSubmitted, vouchers,
    totalBooks, availableBooks, activeBorrows, overdueBorrows, totalFines, unpaidFines, catMap,
    booksCount,
    published, totalSubm, graded, lateSubm, assignments,
    teacherAttMap, teacherSlots,
    events, announcements, notices,
  } = data;

  const attColor = (pct) => pct>=75?'#10B981':pct>=50?'#F59E0B':'#EF4444';
  const fmtRs   = (n) => `Rs ${Number(n).toLocaleString()}`;

  // Sort class attendance
  const best  = classAtt[0];
  const worst = classAtt[classAtt.length-1];

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="reports">

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:22, fontWeight:800, color:'#111827', margin:0 }}>Reports & Analytics</h1>
            <p style={{ color:'#6B7280', fontSize:13, margin:'4px 0 0' }}>Real-time overview of every LMS feature</p>
          </div>
          <button onClick={() => setRefresh(r=>r+1)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1px solid #E5E7EB', background:'#fff', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            <RefreshCw size={14}/> Refresh
          </button>
        </div>

        {/* ── TOP STATS ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:24 }}>
          <StatCard label="Students"       value={students}       icon={Users}        color="#3B6EE8" bg="#EFF6FF"  sub={`${activeClasses} classes`}/>
          <StatCard label="Teachers"       value={activeTeachers} icon={GraduationCap}color="#7C3AED" bg="#F5F3FF"  sub={`${teachers} total`}/>
          <StatCard label="Attendance"     value={`${overallAtt}%`} icon={CheckCircle} color={attColor(overallAtt)} bg={`${attColor(overallAtt)}15`} sub={`${totalSessions} sessions`}/>
          <StatCard label="Fee Collected"  value={fmtRs(totalCollected)} icon={CreditCard} color="#059669" bg="#F0FDF4" sub={`${feePaid} paid`}/>
          <StatCard label="Books"          value={totalBooks}     icon={Library}      color="#0891B2" bg="#ECFEFF"  sub={`${availableBooks} available`}/>
          <StatCard label="Assignments"    value={assignments}    icon={FileText}     color="#F59E0B" bg="#FFFBEB"  sub={`${totalSubm} submitted`}/>
          <StatCard label="Events"         value={events}         icon={Calendar}     color="#DB2777" bg="#FDF2F8"  sub={`${announcements} announcements`}/>
          <StatCard label="Overdue Books"  value={overdueBorrows} icon={AlertCircle}  color={overdueBorrows>0?'#EF4444':'#9CA3AF'} bg={overdueBorrows>0?'#FEF2F2':'#F9FAFB'} sub={`${fmtRs(unpaidFines)} fines`}/>
        </div>

        {/* ── ROW 1: STUDENTS + ATTENDANCE ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

          {/* Student breakdown */}
          <Section title="Student Breakdown" icon={Users} color="#3B6EE8">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:12 }}>By Degree</div>
                {Object.entries(degreeMap).sort((a,b)=>b[1]-a[1]).map(([deg,cnt])=>(
                  <ProgressRow key={deg} label={deg} value={cnt} max={students}
                    color="linear-gradient(90deg,#3B6EE8,#7C3AED)" right={cnt}/>
                ))}
                {Object.keys(degreeMap).length===0&&<div style={{color:'#9CA3AF',fontSize:13}}>No data</div>}
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:12 }}>By Year</div>
                {Object.entries(yearMap).sort((a,b)=>Number(a[0])-Number(b[0])).map(([yr,cnt])=>(
                  <ProgressRow key={yr} label={`Year ${yr}`} value={cnt} max={students}
                    color="linear-gradient(90deg,#10B981,#059669)" right={cnt}/>
                ))}
                {Object.keys(yearMap).length===0&&<div style={{color:'#9CA3AF',fontSize:13}}>No data</div>}
              </div>
            </div>
            {/* Gender + dept */}
            {Object.keys(genderMap).length>0&&(
              <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #F1F5F9'}}>
                <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:12}}>By Gender</div>
                <div style={{display:'flex',gap:12,alignItems:'center'}}>
                  <DonutChart size={80} thickness={14} segments={Object.entries(genderMap).map(([g,v],i)=>({
                    value:v, color:['#3B6EE8','#DB2777','#F59E0B'][i%3],
                  }))}/>
                  <div>
                    {Object.entries(genderMap).map(([g,v],i)=>(
                      <div key={g} style={{display:'flex',alignItems:'center',gap:6,marginBottom:5}}>
                        <div style={{width:10,height:10,borderRadius:'50%',background:['#3B6EE8','#DB2777','#F59E0B'][i%3]}}/>
                        <span style={{fontSize:12,color:'#374151'}}>{g}: <strong>{v}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Attendance overview */}
          <Section title="Attendance Overview" icon={CheckCircle} color={attColor(overallAtt)}>
            {/* Big % */}
            <div style={{ textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:52, fontWeight:900, color:attColor(overallAtt), lineHeight:1 }}>{overallAtt}%</div>
              <div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>Overall Attendance Rate</div>
              <div style={{ background:'#F1F5F9', borderRadius:8, height:10, overflow:'hidden', margin:'10px 0' }}>
                <div style={{ height:'100%', width:`${overallAtt}%`, background:attColor(overallAtt), borderRadius:8, transition:'width 0.6s' }}/>
              </div>
            </div>
            {/* Best / Worst class */}
            {classAtt.length>0&&(
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                {best&&(
                  <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:10,textAlign:'center'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'#059669'}}>🏆 BEST</div>
                    <div style={{fontSize:13,fontWeight:800,color:'#111827',marginTop:2}}>{best.name}</div>
                    <div style={{fontSize:18,fontWeight:900,color:'#10B981'}}>{best.pct}%</div>
                  </div>
                )}
                {worst&&worst.name!==best?.name&&(
                  <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:10,textAlign:'center'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'#EF4444'}}>⚠️ LOWEST</div>
                    <div style={{fontSize:13,fontWeight:800,color:'#111827',marginTop:2}}>{worst.name}</div>
                    <div style={{fontSize:18,fontWeight:900,color:'#EF4444'}}>{worst.pct}%</div>
                  </div>
                )}
              </div>
            )}
            {/* Per class bars */}
            {classAtt.length>0&&(
              <>
                <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:10}}>By Class</div>
                {classAtt.map(c=>(
                  <ProgressRow key={c.name} label={c.name} value={c.pct} max={100}
                    color={attColor(c.pct)} right={`${c.pct}%`}/>
                ))}
              </>
            )}
            {classAtt.length===0&&<div style={{color:'#9CA3AF',fontSize:13}}>No attendance data yet</div>}
            {/* Monthly attendance chart */}
            {Object.keys(monthlyAtt).length>1&&(
              <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #F1F5F9'}}>
                <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:10}}>Monthly Trend</div>
                <BarChart height={90} color={(_,i)=>attColor(last6.map(m=>monthlyAtt[m]?Math.round(monthlyAtt[m].present/monthlyAtt[m].total*100):0)[i]||0)}
                  data={last6.map(m=>({
                    label: m.slice(5),
                    value: monthlyAtt[m] ? Math.round(monthlyAtt[m].present/monthlyAtt[m].total*100) : 0,
                  }))}/>
              </div>
            )}
          </Section>
        </div>

        {/* ── ROW 2: FEES + LIBRARY ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

          {/* Fee analytics */}
          <Section title="Fee Analytics" icon={CreditCard} color="#059669">
            <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16}}>
              <DonutChart size={90} thickness={16} segments={[
                {value:feePaid,    color:'#10B981'},
                {value:feePending, color:'#F59E0B'},
                {value:feeOverdue, color:'#EF4444'},
              ]}/>
              <div>
                {[['Paid',    feePaid,    '#10B981'],
                  ['Pending', feePending, '#F59E0B'],
                  ['Overdue', feeOverdue, '#EF4444']].map(([l,v,c])=>(
                  <div key={l} style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                    <div style={{width:10,height:10,borderRadius:'50%',background:c}}/>
                    <span style={{fontSize:12,color:'#374151'}}>{l}: <strong style={{color:c}}>{v}</strong></span>
                  </div>
                ))}
                {feeSubmitted>0&&<div style={{fontSize:11,color:'#F59E0B',fontWeight:700}}>⚠️ {feeSubmitted} pending review</div>}
              </div>
            </div>
            {[
              {l:'Total Collected', v:fmtRs(totalCollected), c:'#10B981'},
              {l:'Total Pending',   v:fmtRs(totalPending),   c:'#EF4444'},
              {l:'Total Vouchers',  v:vouchers,              c:'#3B6EE8'},
            ].map(r=>(
              <div key={r.l} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #F1F5F9',fontSize:13}}>
                <span style={{color:'#6B7280'}}>{r.l}</span>
                <span style={{fontWeight:800,color:r.c}}>{r.v}</span>
              </div>
            ))}
          </Section>

          {/* Library analytics */}
          <Section title="Library Analytics" icon={Library} color="#0891B2">
            <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16}}>
              <DonutChart size={90} thickness={16} segments={[
                {value:availableBooks,           color:'#10B981'},
                {value:totalBooks-availableBooks, color:'#3B6EE8'},
                {value:overdueBorrows,            color:'#EF4444'},
              ]}/>
              <div>
                {[['Available',  availableBooks,            '#10B981'],
                  ['Borrowed',   totalBooks-availableBooks,  '#3B6EE8'],
                  ['Overdue',    overdueBorrows,             '#EF4444']].map(([l,v,c])=>(
                  <div key={l} style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                    <div style={{width:10,height:10,borderRadius:'50%',background:c}}/>
                    <span style={{fontSize:12,color:'#374151'}}>{l}: <strong style={{color:c}}>{v}</strong></span>
                  </div>
                ))}
              </div>
            </div>
            {[
              {l:'Total Book Titles', v:booksCount,      c:'#0891B2'},
              {l:'Total Copies',      v:totalBooks,      c:'#374151'},
              {l:'Active Borrows',    v:activeBorrows,   c:'#3B6EE8'},
              {l:'Total Fines',       v:fmtRs(totalFines), c:'#F59E0B'},
              {l:'Unpaid Fines',      v:fmtRs(unpaidFines), c:'#EF4444'},
            ].map(r=>(
              <div key={r.l} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #F1F5F9',fontSize:13}}>
                <span style={{color:'#6B7280'}}>{r.l}</span>
                <span style={{fontWeight:800,color:r.c}}>{r.v}</span>
              </div>
            ))}
            {/* Category chart */}
            {Object.keys(catMap).length>0&&(
              <div style={{marginTop:14,paddingTop:14,borderTop:'1px solid #F1F5F9'}}>
                <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:8}}>Books by Category</div>
                <BarChart height={80} showLabels={true} color="#0891B2"
                  data={Object.entries(catMap).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([k,v])=>({label:k,value:v}))}/>
              </div>
            )}
          </Section>
        </div>

        {/* ── ROW 3: ASSIGNMENTS + TEACHER ATTENDANCE ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

          {/* Assignments */}
          <Section title="Assignments" icon={FileText} color="#F59E0B">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              {[
                {l:'Total',     v:assignments, c:'#374151'},
                {l:'Published', v:published,   c:'#3B6EE8'},
                {l:'Submitted', v:totalSubm,   c:'#7C3AED'},
                {l:'Graded',    v:graded,      c:'#10B981'},
                {l:'Late Subs', v:lateSubm,    c:'#EF4444'},
                {l:'Pending',   v:totalSubm-graded, c:'#F59E0B'},
              ].map(s=>(
                <div key={s.l} style={{background:'#F8FAFF',borderRadius:10,padding:'10px 12px',textAlign:'center'}}>
                  <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:10,color:'#9CA3AF',fontWeight:600}}>{s.l}</div>
                </div>
              ))}
            </div>
            {assignments>0&&(
              <BarChart height={90} color={(d)=>d.label==='Graded'?'#10B981':d.label==='Submitted'?'#7C3AED':'#F59E0B'}
                data={[
                  {label:'Total',     value:assignments},
                  {label:'Published', value:published},
                  {label:'Submitted', value:totalSubm},
                  {label:'Graded',    value:graded},
                  {label:'Late',      value:lateSubm},
                ]}/>
            )}
          </Section>

          {/* Teacher attendance */}
          <Section title="Teacher Attendance" icon={GraduationCap} color="#7C3AED">
            {Object.keys(teacherAttMap).length===0 ? (
              <div style={{textAlign:'center',padding:24,color:'#9CA3AF'}}>
                <div style={{fontSize:36,marginBottom:8}}>📊</div>
                <div>No teacher attendance marked yet.</div>
                <div style={{fontSize:12,marginTop:4}}>Teachers mark attendance via Timetable page</div>
              </div>
            ) : (
              Object.entries(teacherAttMap).sort((a,b)=>{
                const pa=a[1].total>0?a[1].present/a[1].total:0;
                const pb=b[1].total>0?b[1].present/b[1].total:0;
                return pb-pa;
              }).map(([tid, t])=>{
                const pct = t.total>0 ? Math.round(t.present/t.total*100) : 0;
                const slots = teacherSlots[tid]||0;
                return (
                  <div key={tid} style={{marginBottom:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <div>
                        <span style={{fontSize:13,fontWeight:700,color:'#111827'}}>{t.name}</span>
                        {slots>0&&<span style={{fontSize:10,color:'#9CA3AF',marginLeft:6}}>{slots} slots/week</span>}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:12,color:'#6B7280'}}>{t.present}/{t.total}</span>
                        <span style={{fontSize:13,fontWeight:900,color:attColor(pct)}}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{background:'#F1F5F9',borderRadius:6,height:7,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:attColor(pct),borderRadius:6,transition:'width 0.5s'}}/>
                    </div>
                  </div>
                );
              })
            )}
          </Section>
        </div>

        {/* ── ROW 4: TEACHERS + COMMUNICATIONS ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

          {/* Teacher stats */}
          <Section title="Teachers" icon={GraduationCap} color="#7C3AED">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
              {[
                {l:'Total',    v:teachers,      c:'#374151'},
                {l:'Active',   v:activeTeachers,c:'#10B981'},
                {l:'Inactive', v:teachers-activeTeachers, c:'#EF4444'},
              ].map(s=>(
                <div key={s.l} style={{background:'#F8FAFF',borderRadius:10,padding:'12px 10px',textAlign:'center'}}>
                  <div style={{fontSize:22,fontWeight:900,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:10,color:'#9CA3AF',fontWeight:600}}>{s.l}</div>
                </div>
              ))}
            </div>
            {Object.keys(deptMap).length>0&&(
              <>
                <div style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:10}}>By Department</div>
                {Object.entries(deptMap).sort((a,b)=>b[1]-a[1]).map(([d,v])=>(
                  <ProgressRow key={d} label={d} value={v} max={teachers}
                    color="linear-gradient(90deg,#7C3AED,#4F8EF7)" right={v}/>
                ))}
              </>
            )}
          </Section>

          {/* Communications */}
          <Section title="Communications" icon={Bell} color="#DB2777">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
              {[
                {l:'Events',        v:events,        c:'#DB2777', e:'🎉'},
                {l:'Announcements', v:announcements, c:'#7C3AED', e:'📢'},
                {l:'Notices',       v:notices,       c:'#3B6EE8', e:'📌'},
                {l:'Classes',       v:activeClasses, c:'#10B981', e:'🏫'},
              ].map(s=>(
                <div key={s.l} style={{background:'#F8FAFF',borderRadius:12,padding:'14px 12px',textAlign:'center',border:'1px solid #E5E7EB'}}>
                  <div style={{fontSize:22,marginBottom:4}}>{s.e}</div>
                  <div style={{fontSize:22,fontWeight:900,color:s.c}}>{s.v}</div>
                  <div style={{fontSize:10,color:'#9CA3AF',fontWeight:600}}>{s.l}</div>
                </div>
              ))}
            </div>
            <BarChart height={90}
              color={(_,i)=>['#DB2777','#7C3AED','#3B6EE8','#10B981'][i%4]}
              data={[
                {label:'Events',   value:events},
                {label:'Announce', value:announcements},
                {label:'Notices',  value:notices},
                {label:'Classes',  value:activeClasses},
              ]}/>
          </Section>
        </div>

        {/* ── MONTHLY STUDENT GROWTH ── */}
        {Object.keys(monthlyStudents).length>1&&(
          <Section title="Student Growth (Last 6 Months)" icon={TrendingUp} color="#3B6EE8">
            <BarChart height={110}
              color="linear-gradient(180deg,#3B6EE8,#7C3AED)"
              data={last6.map(m=>({label:m.slice(5), value:monthlyStudents[m]||0}))}/>
          </Section>
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}